/**
 * `<Avatar>` — image with deterministic-color initials fallback.
 *
 * Foundations spec: design/design-system-foundations.md §9.14.
 * Renders an `<img>` when `src` is provided; on load failure (or when
 * `src` is null/undefined) falls back to upper-cased 1-2 char initials
 * on a hash-derived background color from the existing
 * `lib/initials.ts` palette.
 *
 * 30f Phase B5 — additive only. CandidatePhoto refactor (which wraps
 * this primitive with tenant-aware photo URLs + version cache-buster)
 * is Phase C scope, not 30f.
 *
 * Per HoD ratification Q6: 5 sizes — xs/sm/md/lg/xl. xl is 72px (the
 * RankedCandidateCard hero size; CandidatePhoto.lg = 80px will map
 * down to xl in C3).
 *
 * P2-2.5-sidebar-avatar-primitive (2026-05-12): added `"2xl"` size at
 * 96px to feed `<CandidatePhoto size="xl">` for the Candidate-page
 * sidebar's hero avatar block (previously a hand-rolled gradient circle
 * with initials). The 2xl size is additive — no other size mapping or
 * style was touched.
 */
import { useState } from "react";
import { avatarColor, initials, shade } from "@/lib/initials";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

interface AvatarProps {
  /** Image URL. Null/undefined or load failure -> initials fallback. */
  src?: string | null;
  /** Candidate / user name. Drives initials text + background color. */
  name: string;
  /** Visual size. Default "lg" (48px) — matches the prototype card. */
  size?: AvatarSize;
  /** Optional colored ring around the avatar (e.g. accent on Candidate page header). */
  ringColor?: string;
  /** Optional className passthrough for ad-hoc layout tweaks. */
  className?: string;
}

const SIZE_PX: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 72,
  "2xl": 96,
};

const FONT_SIZE_CLASS: Record<AvatarSize, string> = {
  xs: "text-2xs",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
  xl: "text-xl",
  "2xl": "text-3xl",
};

export function Avatar({
  src,
  name,
  size = "lg",
  ringColor,
  className = "",
}: AvatarProps): JSX.Element {
  const [errored, setErrored] = useState(false);
  const px = SIZE_PX[size];
  const fontClass = FONT_SIZE_CLASS[size];
  const showImage = !!src && !errored;
  const baseColor = avatarColor(name);
  const gradientStop = shade(baseColor, -15);
  const ringStyle = ringColor
    ? { boxShadow: `0 0 0 2px ${ringColor}` }
    : undefined;

  if (showImage) {
    return (
      <span
        className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ${className}`}
        style={{ width: px, height: px, ...ringStyle }}
      >
        <img
          src={src}
          alt={name}
          width={px}
          height={px}
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-text-on-brand ${fontClass} ${className}`}
      style={{
        width: px,
        height: px,
        backgroundImage: `linear-gradient(135deg, ${baseColor} 0%, ${gradientStop} 100%)`,
        ...ringStyle,
      }}
      aria-label={name}
    >
      {initials(name)}
    </span>
  );
}
